/**
 * Contract Template Mapping — admin view (P4.3.1).
 *
 * Signport owns WHICH fields exist on a template. The CRM owns what they mean.
 * This page is where that second half is configured, and it is the only place
 * a mapping is defined — the browser renders the mapping, it never invents one.
 *
 * Two rules this UI exists to make visible:
 *
 *   - Mappings key on the STABLE field_key, not the row id. Signport
 *     regenerates every field id when a template is saved, so an id-keyed
 *     mapping would break every time somebody nudged a box in the editor.
 *   - A required field with no CRM source blocks the send. That is surfaced
 *     here as a warning rather than discovered when a contract is refused.
 *
 * Placeholder editing: Signport exposes no per-field PATCH — the only write is
 * PUT /templates/{id}/fields with the COMPLETE field array. The backend does
 * that read-modify-write. On blur we save, then replace the input's value with
 * what the SERVER echoed back, so the UI can never show a placeholder that did
 * not persist. A failed save restores the previously persisted value.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  RefreshCw, AlertTriangle, CheckCircle2, Link2Off, Loader2, Trash2,
  FileSignature, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAP = `${API}/contract-mapping`;

const UNMAPPED = '__unmapped__';

const groupLabel = {
  merchant: 'Merchant',
  guarantor: 'Guarantor',
  terms: 'Contract Terms',
  document: 'Document',
};

const statusBadge = (status) => {
  if (status === 'mapped') {
    return { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Mapped' };
  }
  return { cls: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Unmapped' };
};

export default function ContractTemplateMapping() {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [report, setReport] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [transforms, setTransforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);

  // Placeholder drafts, keyed by field_key. A field is present here only while
  // the user is editing it; on blur the entry is dropped and the row shows the
  // value the server persisted.
  const [drafts, setDrafts] = useState({});
  const [savingPlaceholder, setSavingPlaceholder] = useState(null);
  const inFlight = useRef({});

  const loadCatalogue = useCallback(async () => {
    try {
      const res = await axios.get(`${MAP}/canonical-fields`);
      setCatalogue(res.data.fields || []);
      setTransforms(res.data.transforms || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load CRM field list');
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${MAP}/templates`);
      const list = res.data.templates || [];
      setTemplates(list);
      setTemplateId((current) => current || (list[0] && list[0].id) || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load Signport templates');
    }
  }, []);

  const loadReport = useCallback(async (id, { quiet } = {}) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${MAP}/templates/${encodeURIComponent(id)}`);
      setReport(res.data);
      setDrafts({});
      if (!quiet) toast.success('Fields refreshed from Signport');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read the template schema');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCatalogue(); loadTemplates(); }, [loadCatalogue, loadTemplates]);
  useEffect(() => { if (templateId) loadReport(templateId, { quiet: true }); },
    [templateId, loadReport]);

  const grouped = useMemo(() => {
    const out = {};
    catalogue.forEach((f) => {
      (out[f.group] = out[f.group] || []).push(f);
    });
    return out;
  }, [catalogue]);

  const setMapping = async (fieldKey, patch) => {
    setSavingKey(fieldKey);
    try {
      const res = await axios.put(
        `${MAP}/templates/${encodeURIComponent(templateId)}/fields/${encodeURIComponent(fieldKey)}`,
        patch);
      setReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the mapping');
    } finally {
      setSavingKey(null);
    }
  };

  const clearMapping = async (fieldKey) => {
    setSavingKey(fieldKey);
    try {
      const res = await axios.delete(
        `${MAP}/templates/${encodeURIComponent(templateId)}/fields/${encodeURIComponent(fieldKey)}`);
      setReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not clear the mapping');
    } finally {
      setSavingKey(null);
    }
  };

  const pruneStale = async () => {
    try {
      const res = await axios.post(
        `${MAP}/templates/${encodeURIComponent(templateId)}/prune-stale`);
      setReport(res.data);
      toast.success(`Removed ${(res.data.pruned || []).length} stale mapping(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not prune stale mappings');
    }
  };

  /**
   * Persist one placeholder, then adopt whatever the server actually stored.
   *
   * Never optimistic: the row is updated from the response, so a save that
   * failed leaves the previously persisted text on screen rather than the
   * text the user typed.
   */
  const savePlaceholder = async (row) => {
    const draft = drafts[row.field_key];
    if (draft === undefined) return;                     // not edited
    const persisted = row.placeholder_text || '';
    if (draft === persisted) {                           // unchanged — no write
      setDrafts((d) => { const n = { ...d }; delete n[row.field_key]; return n; });
      return;
    }
    if (inFlight.current[row.field_key]) return;
    inFlight.current[row.field_key] = true;
    setSavingPlaceholder(row.field_key);
    try {
      const res = await axios.put(
        `${MAP}/templates/${encodeURIComponent(templateId)}/fields/${encodeURIComponent(row.field_key)}/placeholder`,
        { placeholder: draft });
      const saved = res.data.placeholder_text || '';
      setReport((prev) => (prev ? {
        ...prev,
        rows: prev.rows.map((r) => (r.field_key === row.field_key
          // field_id too: Signport regenerates it on every save.
          ? { ...r, placeholder_text: saved, field_id: res.data.field_id }
          : r)),
      } : prev));
      setDrafts((d) => { const n = { ...d }; delete n[row.field_key]; return n; });
      toast.success('Placeholder saved');
    } catch (e) {
      // Restore the persisted value — do not leave the failed edit on screen.
      setDrafts((d) => { const n = { ...d }; delete n[row.field_key]; return n; });
      toast.error(e.response?.data?.detail || 'Placeholder did not save');
    } finally {
      delete inFlight.current[row.field_key];
      setSavingPlaceholder(null);
    }
  };

  const rows = report?.rows || [];
  const stale = report?.stale || [];
  const requiredUnmapped = report?.required_unmapped_count || 0;

  return (
    <Layout>
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <FileSignature className="h-6 w-6" /> Contract Template Mapping
            </h1>
            <p className="text-sm text-muted-foreground">
              Signport defines the fields. The CRM defines what fills them.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger className="w-[280px]" data-testid="template-picker">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} {t.company_id ? `· ${t.company_id}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => loadReport(templateId)}
              disabled={loading || !templateId} data-testid="refresh-fields">
              {loading
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <RefreshCw className="h-4 w-4" />}
              <span className="ml-2">Refresh fields from Signport</span>
            </Button>
          </div>
        </div>

        {report && (
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
              {report.mapped_count} mapped
            </Badge>
            <Badge className="bg-amber-100 text-amber-800 border-amber-300">
              {report.unmapped_count} unmapped
            </Badge>
            {stale.length > 0 && (
              <Badge className="bg-slate-200 text-slate-700 border-slate-300">
                {stale.length} stale
              </Badge>
            )}
            <span className="text-xs text-muted-foreground ml-2">
              {report.field_count} field(s) on {report.template_name || report.template_id}
              {report.company_id ? ` · ${report.company_id}` : ''}
            </span>
          </div>
        )}

        {requiredUnmapped > 0 && (
          <Card className="border-red-300 bg-red-50">
            <CardContent className="p-4 flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 text-red-600 mt-0.5" />
              <div className="text-sm">
                <div className="font-medium text-red-800">
                  {requiredUnmapped} required field
                  {requiredUnmapped === 1 ? '' : 's'} without a CRM source
                </div>
                <div className="text-red-700">
                  Contracts from this template cannot be sent until these are mapped.
                  A partially populated legal document is never sent.
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {stale.length > 0 && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <Link2Off className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-medium text-amber-900">
                      {stale.length} mapping{stale.length === 1 ? '' : 's'} point at
                      a field Signport no longer reports
                    </div>
                    <div className="text-amber-800">
                      {stale.map((s) => s.label || s.field_key).join(', ')} — these are
                      never re-matched to a similar field automatically.
                    </div>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={pruneStale}
                  data-testid="prune-stale">
                  <Trash2 className="h-4 w-4 mr-1" /> Remove
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr className="text-left">
                  <th className="p-3 font-medium">Signport Field</th>
                  <th className="p-3 font-medium">CRM Source</th>
                  <th className="p-3 font-medium">Format</th>
                  <th className="p-3 font-medium">Placeholder text</th>
                  <th className="p-3 font-medium">Required</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted-foreground">
                      {loading ? 'Reading the template schema…'
                        : 'No fields on this template yet. Add them in Signport, then refresh.'}
                    </td>
                  </tr>
                )}
                {rows.map((row) => {
                  const badge = statusBadge(row.status);
                  const draft = drafts[row.field_key];
                  const value = draft !== undefined ? draft : (row.placeholder_text || '');
                  return (
                    <tr key={row.field_key} className="border-b last:border-0 align-top"
                      data-testid={`map-row-${row.field_key}`}>
                      <td className="p-3">
                        <div className="font-medium">{row.label}</div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {row.field_key}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {row.type}{row.page ? ` · page ${row.page}` : ''}
                        </div>
                      </td>

                      <td className="p-3 min-w-[240px]">
                        <Select
                          value={row.canonical || UNMAPPED}
                          onValueChange={(v) => (v === UNMAPPED
                            ? clearMapping(row.field_key)
                            : setMapping(row.field_key, { canonical: v }))}
                        >
                          <SelectTrigger data-testid={`source-${row.field_key}`}>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={UNMAPPED}>— Unmapped —</SelectItem>
                            {Object.keys(grouped).map((group) => (
                              <React.Fragment key={group}>
                                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                  {groupLabel[group] || group}
                                </div>
                                {grouped[group].map((f) => (
                                  <SelectItem key={f.key} value={f.key}>
                                    {f.label}{f.sensitive ? ' · sensitive' : ''}
                                  </SelectItem>
                                ))}
                              </React.Fragment>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>

                      <td className="p-3 min-w-[140px]">
                        <Select
                          value={row.transform || 'plain'}
                          onValueChange={(v) => setMapping(row.field_key, { transform: v })}
                        >
                          <SelectTrigger data-testid={`transform-${row.field_key}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {transforms.map((t) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>

                      <td className="p-3 min-w-[220px]">
                        <div className="relative">
                          <Input
                            value={value}
                            placeholder="Shown to the signer until they type"
                            onChange={(e) => setDrafts((d) => ({
                              ...d, [row.field_key]: e.target.value,
                            }))}
                            onBlur={() => savePlaceholder(row)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur();
                              if (e.key === 'Escape') {
                                setDrafts((d) => {
                                  const n = { ...d }; delete n[row.field_key]; return n;
                                });
                              }
                            }}
                            data-testid={`placeholder-${row.field_key}`}
                          />
                          {savingPlaceholder === row.field_key && (
                            <Loader2 className="h-4 w-4 animate-spin absolute right-2 top-2.5 text-muted-foreground" />
                          )}
                        </div>
                      </td>

                      <td className="p-3">
                        {row.required
                          ? <Badge className="bg-slate-800 text-white">Yes</Badge>
                          : <span className="text-muted-foreground">No</span>}
                      </td>

                      <td className="p-3">
                        <Badge className={badge.cls}>
                          {row.status === 'mapped'
                            ? <CheckCircle2 className="h-3 w-3 mr-1 inline" />
                            : <AlertTriangle className="h-3 w-3 mr-1 inline" />}
                          {badge.label}
                        </Badge>
                        {row.canonical_label && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {row.canonical_label}
                          </div>
                        )}
                      </td>

                      <td className="p-3 text-right">
                        {savingKey === row.field_key && (
                          <Loader2 className="h-4 w-4 animate-spin inline" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground">
          Mappings are stored against the stable Signport field key, so they survive
          a template save (Signport regenerates every field id when a template is
          saved). Money never comes from here — variable terms are read from the
          confirmed contract terms for each contract, and a contract never carries
          a Deal #, which is allocated only when a transaction is funded.
        </p>
      </div>
    </Layout>
  );
}
